import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography, Button, Stack, Divider } from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import { apiGetShowcaseNews } from "../../../../api/News";
import { getLangFromCookie } from "../../../../Utils/LangCookie";
import { getPrimaryColor } from "../../../GlobalFunction/Theme";

/* ---- language helpers ---- */
function normalizeLang(lang) {
  const v = String(lang || "")
    .trim()
    .toUpperCase();
  return v === "ZH" || v === "EN" || v === "JP" ? v : "EN";
}

/* ---- label translations ---- */
const LABELS = {
  title: { ZH: "最新資訊", EN: "Latest News", JP: "最新情報" },
  more: { ZH: "查看更多", EN: "View More", JP: "もっと見る" },
  loading: { ZH: "載入中...", EN: "Loading...", JP: "読み込み中..." },
  empty: {
    ZH: "目前沒有最新資訊",
    EN: "No news yet",
    JP: "ニュースはありません",
  },
};

const safeSeg = (s) => encodeURIComponent(String(s || "").trim());

/** only allow these 3 values to ever appear in URL */
const VALID_TYPES = new Set(["衛生資訊", "最新消息", "活動資訊"]);

/** normalize & validate */
const normalizeNewsTypeStrict = (raw, fallback = "最新消息") => {
  const t = String(raw ?? "").trim();
  if (VALID_TYPES.has(t)) return t;

  const fb = String(fallback ?? "最新消息").trim();
  return VALID_TYPES.has(fb) ? fb : "最新消息";
};

/** pick type from row carefully; if row is weird, fallback to section prop */
const pickRowType = (row) =>
  row?.newsType ??
  row?.type ??
  row?.news_type ??
  row?.newsTypeName ??
  row?.category ??
  "";

/** normalize slug */
const normalizeSlug = (row) => String(row?.slug || "").trim();

const buildNewsHref = (newsType, slug) =>
  `/最新資訊/${safeSeg(newsType)}/${safeSeg(slug)}`;

function formatNewsDate(value, lang) {
  if (!value) return "";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  if (lang === "ZH") {
    const rocY = d.getFullYear() - 1911;
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${rocY}年${m}月${day}日`;
  }

  if (lang === "JP") {
    return d.toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function firstTagName(n) {
  if (!Array.isArray(n?.tags) || n.tags.length === 0) return "";
  const first = n.tags[0];
  if (typeof first === "string") return first;
  if (first && typeof first === "object") return first.name || "";
  return "";
}

export default function LPnewsB2B({ titleColor, bgcolor, fonts, newsType }) {
  const location = useLocation();

  const [lang, setLang] = useState(() =>
    normalizeLang(getLangFromCookie() || "EN"),
  );
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const sectionType = useMemo(
    () => normalizeNewsTypeStrict(newsType, "最新消息"),
    [newsType],
  );

  useEffect(() => {
    const handleLangChange = () => {
      setLang(normalizeLang(getLangFromCookie() || "EN"));
    };

    window.addEventListener("app:languageChanged", handleLangChange);
    return () =>
      window.removeEventListener("app:languageChanged", handleLangChange);
  }, []);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      try {
        setLoading(true);

        const data = await apiGetShowcaseNews({
          limit: 3,
          newsType: sectionType || undefined,
          lang,
          skipLoading: true,
        });

        if (!alive) return;
        setItems(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("[LPnews] showcase fetch failed:", e);
        if (!alive) return;
        setItems([]);
      } finally {
        if (alive) setLoading(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [sectionType, lang]);

  const titleLabel = LABELS.title[lang] || LABELS.title.EN;
  const moreLabel = LABELS.more[lang] || LABELS.more.EN;
  const loadingLabel = LABELS.loading[lang] || LABELS.loading.EN;
  const emptyLabel = LABELS.empty[lang] || LABELS.empty.EN;

  const list = useMemo(() => items.slice(0, 3), [items]);

  const _routeKey = `lpnews-${location.pathname}-${lang}`;

  return (
    <Box
      key={_routeKey}
      component="section"
      bgcolor={bgcolor}
      overflow="hidden"
      sx={{
        px: { xs: 0, md: 0 },
        py: { xs: 5, md: 4 },
        "--left": { xs: "17.2%", md: "17.2%" },
        "--cta": { xs: "auto", md: "160px" },
        "--right": { xs: "0px", md: "15%" },
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "var(--left) 1fr var(--cta) var(--right)",
          },
          alignItems: "center",
          bgcolor: bgcolor,
          borderTopLeftRadius: 6,
          borderTopRightRadius: 6,
          py: { xs: 1.25, md: 1.5 },
          borderBottom: 1,
          borderColor: "divider",
          px: { xs: 2, md: 0 },
        }}
      >
        <Box sx={{ display: { xs: "none", md: "block" } }} />
        <Box sx={{ pl: { md: 4 } }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontFamily: fonts?.[lang],
              color: titleColor,
              textAlign: "left",
            }}
          >
            {titleLabel}
          </Typography>
        </Box>

        <Box sx={{ display: { xs: "none", md: "block" } }} />
        <Box sx={{ display: { xs: "none", md: "block" } }} />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateAreas: {
            xs: `"content"
                 "cta"`,
            md: `"left content cta right"`,
          },
          gridTemplateColumns: {
            xs: "1fr",
            md: "var(--left) 1fr var(--cta) var(--right)",
          },
          alignItems: "stretch",
          bgcolor: (t) => (t.palette.mode === "dark" ? "grey.800" : "grey.100"),
          borderBottomLeftRadius: 6,
          borderBottomRightRadius: 6,
          py: { xs: 1.5, md: 2.5 },
          pl: { md: 5 },
        }}
      >
        <Box
          sx={{
            gridArea: "content",
            px: { xs: 2, md: 0 },
            pr: { md: 3 },
          }}
        >
          <Stack spacing={{ xs: 1.25, md: 1.5 }}>
            {loading && (
              <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
                {loadingLabel}
              </Typography>
            )}

            {!loading && list.length === 0 && (
              <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
                {emptyLabel}
              </Typography>
            )}

            {list.map((n, idx) => {
              const finalType = normalizeNewsTypeStrict(
                pickRowType(n),
                sectionType,
              );
              const dateText = formatNewsDate(n.postDate, lang);
              const normalizedSlug = normalizeSlug(n);
              const href = buildNewsHref(finalType, normalizedSlug);
              const tagLabel = firstTagName(n);

              return (
                <Box key={n.id ?? `${finalType}-${normalizedSlug}-${idx}`}>
                  <Box
                    component={RouterLink}
                    to={href}
                    sx={{
                      display: { xs: "block", md: "flex" },
                      gap: { md: 2 },
                      alignItems: { md: "center" },
                      width: "100%",
                      textDecoration: "none",
                      cursor: "pointer",
                      py: { xs: 0.25, md: 0.1 },

                      "&, &:visited, &:active": {
                        textDecoration: "none",
                      },

                      "& .lpnews-date": {
                        color: "text.primary",
                        transition: "color 0.2s ease",
                      },

                      "& .lpnews-titlewrap": {
                        color: "text.primary",
                        transition: "color 0.2s ease",
                      },

                      "&:hover .lpnews-date": {
                        color: getPrimaryColor("b2b"),
                      },

                      "&:hover .lpnews-titlewrap": {
                        color: getPrimaryColor("b2b"),
                      },
                    }}
                  >
                    <Typography
                      component="span"
                      className="lpnews-date"
                      sx={{
                        fontFamily: fonts?.[lang],
                        fontWeight: 400,
                        display: "block",
                        mb: { xs: 0.25, md: 0 },
                        minWidth: { xs: "auto", md: 180 },
                      }}
                    >
                      {dateText}
                    </Typography>

                    <Box
                      className="lpnews-titlewrap"
                      sx={{
                        fontFamily: fonts?.[lang],
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 2,
                        lineHeight: 1.5,
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                        mt: { xs: 0.25, md: 0 },
                        flex: { md: 1 },
                        minWidth: 0,
                      }}
                    >
                      {tagLabel ? (
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 1,
                            height: 22,
                            bgcolor: getPrimaryColor("b2b"),
                            color: "#fff",
                            fontWeight: 400,
                            fontFamily: fonts?.[lang],
                            fontSize: 15,
                            lineHeight: "22px",
                            flexShrink: 0,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {tagLabel}
                        </Box>
                      ) : null}

                      <Box
                        component="span"
                        sx={{
                          minWidth: 0,
                          color: "inherit",
                          fontFamily: fonts?.[lang],
                        }}
                      >
                        {n.title}
                      </Box>
                    </Box>
                  </Box>

                  {idx !== list.length - 1 && <Divider sx={{ mt: 1.0 }} />}
                </Box>
              );
            })}
          </Stack>
        </Box>

        <Box
          sx={{
            gridArea: "cta",
            display: "flex",
            alignItems: { xs: "center", md: "flex-end" },
            justifyContent: "flex-end",
            pt: { xs: 1, md: 0 },
          }}
        >
          <Button
            component={RouterLink}
            to="/最新資訊/"
            variant="contained"
            endIcon={<ArrowForwardIosIcon />}
            sx={{
              px: 1.5,
              py: 0.25,
              borderRadius: 0,
              clipPath:
                "polygon(0 0, calc(100% - 16px) 0, 100% 50%, calc(100% - 16px) 100%, 0 100%)",
              bgcolor: "grey",
              color: "#fff",
              fontSize: 14,
              fontWeight: 700,
              whiteSpace: "nowrap",
              boxShadow: "none",
              display: "inline-flex",
              alignItems: "center",
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: getPrimaryColor("b2b"),
                filter: "brightness(0.95)",
                boxShadow: "none",
              },
              "& .MuiButton-endIcon": {
                ml: 0.5,
              },
            }}
          >
            {moreLabel.toUpperCase()}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
